import React, { useState } from 'react';
import {
    Box, TextField, Button, Grid, Card, CardMedia, CardContent, Typography,
    InputAdornment, CircularProgress
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, GridView as GridViewIcon } from '@mui/icons-material';
import { useProducts } from '../../hooks/useProducts';
import Pagination from '../common/Pagination';

const ProductSelector = ({ onAddProduct, clientId, channel }) => {
    const {
        products, loading, error,
        searchTerm, setSearchTerm,
        count, page, setPage
    } = useProducts(clientId, channel);

    // Local state for quantities input per product
    const [quantities, setQuantities] = useState({});

    const handleQuantityChange = (productId, val) => {
        setQuantities({ ...quantities, [productId]: val });
    };

    const handleAddClick = (product) => {
        const qty = parseFloat(quantities[product.id] || 0);
        if (qty > 0) {
            onAddProduct(product, qty);
            setQuantities({ ...quantities, [product.id]: '' });
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search and Filter Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <TextField
                    fullWidth
                    placeholder="Buscar productos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    variant="outlined"
                    size="small"
                />
            </Box>

            {/* Product Grid */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
                {error ? (
                    <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>{error}</Typography>
                ) : loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : products.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No se encontraron productos
                    </Typography>
                ) : (
                <Grid container spacing={2}>
                    {products.map((product) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {product.image_url ? (
                                    <CardMedia
                                        component="img"
                                        height="120"
                                        image={product.image_url}
                                        alt={product.name}
                                        sx={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        height: 120,
                                        bgcolor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#cbd5e0'
                                    }}>
                                        <GridViewIcon sx={{ fontSize: '3rem' }} />
                                    </Box>
                                )}
                                <CardContent sx={{ flex: 1, p: 1.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">{product.name}</Typography>
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" color="primary">${parseFloat(product.price_per_kg).toFixed(2)}</Typography>
                                        <Typography variant="caption">/{product.unit}</Typography>
                                    </Box>
                                </CardContent>
                                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="0"
                                        value={quantities[product.id] || ''}
                                        onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                        inputProps={{ min: 0 }}
                                        fullWidth
                                     />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleAddClick(product)}
                                        disabled={!quantities[product.id] || parseFloat(quantities[product.id]) <= 0}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                )}
            </Box>

            {/* Pagination Controls */}
            {count > 0 && (
                <Pagination
                    count={count}
                    page={page}
                    rowsPerPage={20}
                    onPageChange={(e, newPage) => setPage(newPage)}
                />
            )}
        </Box>
    );
};

export default ProductSelector;
